import React, {useContext, useEffect, useRef} from "react";
import {Animated, Platform, View} from "react-native";
import {getTranslation} from "../helpers/getTranslation";
import PtCallout from "../components/core/pt-callout/pt-callout";
import UserContext from "../helpers/UserContext";
import {useDispatch, useSelector} from "react-redux";
import {SafeAreaView} from "react-native-safe-area-context";
import styles from "./screen-styles";
import PtHeader from "../components/pt-header/pt-header";
import {styleVars} from "../app-style-vars";
import Login from "../components/core/Login/login";
import {getCustomerDataFromApi} from "../actions/customerDataActions";
import { Role } from "../model/Role";
import {getSubdomain} from "../helpers/getSubdomain";
import PtTermsOfUse from "../components/pt-terms-of-use/pt-terms-of-use";
import PtPrivacyPolicy from "../components/pt-privacy-policy/pt-privacy-policy";
import PtImprint from "../components/pt-imprint/pt-imprint";
import PtPrivacySettings from "../components/pt-privacy-policy/pt-privacy-settings";
import PtText from "../components/core/pt-text/pt-text";
const showBoundaries = null;

/** actual page content should be put inside ScreenTemplate tag,
 * each component/section wrapped in a
 * <View style={[styles.pageContentSlotContainer, showBoundaries]}> tag
 */

const featuresCache = () => {return require('../assets/countryConfigs.json')}
const getCountryFeatures = (featuresObject: any, country: string) => {
  const availableFeatures = [];
  console.log('----------- the available features = ', featuresObject, ' --- country = ', country);
  Object.keys(featuresObject).map((featureKey) => {
    if(featuresObject[featureKey][country.toLowerCase()]) {
      availableFeatures.push(featureKey);
    }
  })
  return availableFeatures;
}


export default function LoginScreen() {

  const notLoggedInMessage = getTranslation('no.auth.warning.text') || 'You are currently not logged in. Therefore, some features have been disabled or have limited functionality.';
  const isAuthenticated: boolean = useSelector((state:any) => state.authReducer.isAuthenticated);

  const {userData, updateUserData} = useContext(UserContext)

  useEffect(() => {
    if(isAuthenticated !== userData.authenticated) {
      console.log('--- update user data from login screen (authenticated)');
      updateUserData({...userData, authenticated: isAuthenticated})
    }
  },[isAuthenticated])

  /** we need to get customer data right after authorization and update userContext */
  const customerData: any = useSelector((state:any) => state.customerDataReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated && !customerData.dataCollected) {
      getCustomerDataFromApi(dispatch);
    }
  }, [isAuthenticated]);
  useEffect(() => {
    if(isAuthenticated && customerData.dataCollected) {
      /** we have to select one from the list of siteUIDs received from backend */
      const subdomain = getSubdomain();
      const baseUID = customerData.siteUid.indexOf(subdomain) < 0 ? customerData.siteUid[0] : userData.country;
      const availableCountriesFromUserData = customerData.country.map((country) => (country.isocode.toLowerCase()));
      console.log('---- the available countries = ', availableCountriesFromUserData);
      const baseCountry = availableCountriesFromUserData.indexOf(subdomain) < 0 ? baseUID : subdomain;
      console.log('--- the base country = ', baseCountry, '  --- subdomain = ', userData.country);
      console.log('--- update user data from login screen (customer data)');
      const canImpersonate =
        customerData.roles.includes(Role.PORTAL_MASTER) ||
        customerData.roles.includes(Role.SALES_REP) ||
        customerData.roles.includes(Role.HEADQUARTERS) ||
        customerData.roles.includes(Role.DISTRIBUTOR) ||
        customerData.roles.includes(Role.DI_SALES_REP);
      /** for the moment we're not allowing Distributors to use impersonation */
      // customerData.roles.includes(Role.DISTRIBUTOR)
      const mustImpersonate =
        customerData.roles.includes(Role.PORTAL_MASTER) ||
        customerData.roles.includes(Role.SALES_REP);
      console.log('--- do we know the features here? country =  ', customerData.country);
      updateUserData({
        ...userData,
        availableCountries: customerData.country,
        country: baseCountry,
        availableSiteUIDs: customerData.siteUid,
        baseSiteUID: baseUID,
        dataCollected: customerData.dataCollected,
        features: getCountryFeatures(featuresCache(), baseCountry),
        canImpersonate: canImpersonate,
        mustImpersonate: mustImpersonate,
        orderRestricted: customerData.roles.includes(Role.ORDER_RESTRICTED),
        orderScenarios: customerData.orderScenarios
      })
    }
  }, [isAuthenticated, customerData.dataCollected])


  /** fixing header on scroll */
  const headerFixedHeight = styleVars.headerFixedSectionHeight;
  const headerDiscardHeight = 0;
  const headerHeight = headerFixedHeight + headerDiscardHeight;
  const scrollY = new Animated.Value(0);
  const scrollYRef = useRef(scrollY);
  const handleScroll = Animated.event(
    [{
      nativeEvent: {
        contentOffset: {y: scrollYRef.current},
      },
    },],
    {
      useNativeDriver: true,
    },
  );
  const translateY = scrollYRef.current.interpolate({
    inputRange: [0, headerHeight, headerHeight + 1],
    outputRange: [0, -headerDiscardHeight, -headerDiscardHeight],
  });

  const headerOptions = {
    menuArea: null,
    headerTitle: 'Login',
    headerIcons: [],
    showLogo: false,
    nonavigation: true,
  };

  return (
    <SafeAreaView style={[styles.pageContainer, styles.pageContainerFullScreen, showBoundaries]}>
      <Animated.View style={[styles.fixedHeaderContainer, {transform:[{translateY}]}]}>
        <PtHeader headerOptions={headerOptions}/>
      </Animated.View>
      <Animated.ScrollView
        contentContainerStyle={{paddingBottom: Platform.OS !== 'web' ? headerHeight : 0, minHeight: '100%'}}
        style={[styles.pageScrollContainer, showBoundaries, {paddingTop: headerHeight},]}
        scrollEventThrottle={16}
        onScroll={handleScroll}
      >

        <View style={[styles.inScrollContentContainer, {justifyContent: 'space-between'}]}>

          {!isAuthenticated ?
              <View>
                <PtCallout type={'warning'} message={notLoggedInMessage}/>
                <Login/>
              </View>
              : null
          }

          <View style={[styles.pageContentSlotContainer, showBoundaries, {marginTop: 16, marginBottom: 32}]}>
            <View style={{paddingHorizontal: 8, paddingVertical: 8, backgroundColor: styleVars.boschInfoBlue, marginBottom: 16}}>
              <PtText>{getTranslation('legal.pages.info.message') || "Same legal regulations apply as stated in our main B2B portal."}</PtText>
            </View>
            <View style={{borderTopColor: styleVars.boschLightGray, borderTopWidth: 1, paddingTop: 16, flexDirection: 'row', flexWrap: 'wrap'}}>
              <PtTermsOfUse/>
              <PtPrivacyPolicy/>
              <PtImprint/>
              {Platform.OS === 'web' && <PtPrivacySettings/>}
            </View>
          </View>

        </View>

      </Animated.ScrollView>
    </SafeAreaView>
  )
}
